// VAT on a cost passed on to a tenant (Lewis, 2026-10-06, after a review by a
// VAT specialist): the VAT on the supplier's invoice never decides the VAT of
// the recharge itself. They are two layers. The supplier's invoice is what the
// landlord paid (EUR 80 + EUR 16,80 VAT); the recharge is what the tenant is
// asked, and its VAT follows the lease, not the invoice.
//
// - A lease without VAT (residential, the common case): the tenant pays the
//   real cost, the supplier's VAT included, and the landlord adds no VAT of
//   its own (EUR 96,80 at 0%). The supplier's VAT is only shown for
//   information. The settlement already billed costs this way; a cost billed
//   right away now does the same.
// - A VAT-taxable lease (the optional regime for professional property): the
//   recharge may carry the lease's VAT on the amount without VAT, or be a
//   separate supply at its own rate. Rentio cannot tell which, so the broker
//   chooses, if need be after asking their accountant.

import { fromCents, toCents, vatOfAmount } from "./money.utils";

// The rates a broker can charge on a recharge to a VAT-taxable lease.
export const OUTGOING_VAT_RATES = [6, 12, 21];

// How a recharge is billed: "none" = no VAT of our own (the real cost, the
// supplier's VAT included), or a VAT rate on the amount without VAT.
export type TOutgoingVat = "none" | number;

export const NO_OWN_VAT: TOutgoingVat = "none";

interface ILeaseVatLine {
  priceVat?: number | null;
  priceNoVatType?: string | null;
}

// The lease fields this needs, as the contract API returns them.
export interface ILeaseVatSource extends ILeaseVatLine {
  costsCommonVat?: number | null;
  costsCommonNoVatType?: string | null;
  costsNonCommonVat?: number | null;
  costsNonCommonNoVatType?: string | null;
  additionalCosts?: ILeaseVatLine[] | null;
}

// A lease line carries VAT when it has a rate and no "no VAT" reason.
const chargesVat = (params: {
  vatPct?: number | null;
  noVatType?: string | null;
}): boolean => !params.noVatType && (Number(params.vatPct) || 0) > 0;

// The lease is VAT-taxable: it charges VAT on its rent or on one of its cost
// lines. A residential lease charges none.
export const leaseChargesVat = (lease: ILeaseVatSource): boolean =>
  chargesVat({ vatPct: lease.priceVat, noVatType: lease.priceNoVatType }) ||
  chargesVat({
    vatPct: lease.costsCommonVat,
    noVatType: lease.costsCommonNoVatType,
  }) ||
  chargesVat({
    vatPct: lease.costsNonCommonVat,
    noVatType: lease.costsNonCommonNoVatType,
  }) ||
  (lease.additionalCosts ?? []).some(line =>
    chargesVat({ vatPct: line.priceVat, noVatType: line.priceNoVatType }),
  );

export interface ICostPayment {
  // What the payment order asks, before the VAT it adds itself.
  amount: number;
  // The VAT the payment order adds (0 = none of our own).
  vatPct: number;
  // The supplier's VAT inside `amount` when no VAT of our own is added, shown
  // for information only (0 otherwise).
  supplierVat: number;
}

// The payment order for a tenant's part of a cost, from that part without
// VAT. Without VAT of our own it asks that part plus its share of the
// supplier's VAT, at 0%: EUR 80 + 21% supplier VAT asks EUR 96,80. Those cents
// are ours to set (the payment adds no VAT), so they follow the settlement:
// `supplierVat` is the tenant's part of the cost's VAT split over all its
// parties (costVat.utils supplierVatByLease), which makes a cost billed right
// away ask exactly what the settlement would. Without it, the VAT of this part
// alone, rounded half-up on the cent like every other VAT figure: EUR 40,50 at
// 21% carries EUR 8,51, so the payment asks EUR 49,01, not the EUR 49,00 a
// rounding of 40,50 x 1,21 in floating point gave.
export const costPaymentOf = (params: {
  amountExclVat: number;
  supplierVatPct: number;
  outgoingVat: TOutgoingVat;
  supplierVat?: number;
}): ICostPayment => {
  const { amountExclVat, supplierVatPct, outgoingVat, supplierVat } = params;
  const amountCents = toCents(amountExclVat);
  if (outgoingVat === NO_OWN_VAT) {
    const supplierVatCents = toCents(
      supplierVat ?? vatOfAmount({ amountExclVat, vatPct: supplierVatPct }),
    );
    return {
      amount: fromCents(amountCents + supplierVatCents),
      vatPct: 0,
      supplierVat: fromCents(supplierVatCents),
    };
  }
  return {
    amount: fromCents(amountCents),
    vatPct: outgoingVat,
    supplierVat: 0,
  };
};
